import { Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, cx } from "../../../components/ui";
import type { DataArray, Definition, Issue } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
const cell = "w-full h-8 rounded-md border border-line bg-surface px-2 text-sm";
const ID = /^[A-Za-z][A-Za-z0-9_]*$/;

/** Steps that use an array: run for each of its rows, or send all its rows. */
export function arrayUses(def: Definition, name: string) {
  const steps = [...def.trunk, ...(def.branches ?? []).flatMap((b) => b.steps)];
  return steps.filter((s) => s.repeat?.array === name || (s.infor?.fields ?? []).some((f) => f.value.kind === "arrayRows" && f.value.array === name)).map((s) => s.name);
}

/**
 * Arrays (docs/phase5-plan.md §3, legacy ARRAY): small tables a step can run once per row of (multiple runs), or send
 * whole as fields name1..N. Each row has a value and its attributes.
 */
export function ArraysEditor({ def, issues, onChange }: { def: Definition; issues: Issue[]; onChange: (d: Definition) => void }) {
  const arrays = def.arrays ?? [];
  const [selected, setSelected] = useState<string | null>(arrays[0]?.name ?? null);
  const a = arrays.find((x) => x.name === selected);
  const save = (next: DataArray[]) => onChange({ ...def, arrays: next.length ? next : undefined });
  const add = () => {
    let n = arrays.length + 1;
    while (arrays.some((x) => x.name === `LIST${n}`)) n++;
    save([...arrays, { name: `LIST${n}`, attrs: ["Code"], rows: [{ value: "", attrs: { Code: "" } }] }]);
    setSelected(`LIST${n}`);
  };
  return (
    <div className={cx("grid gap-4", a ? "lg:grid-cols-[18rem_minmax(0,1fr)]" : "")}>
      <div className="space-y-3">
        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          {arrays.map((x) => {
            const bad = issues.some((i) => i.where === `array ${x.name}` && i.level === "error");
            const uses = arrayUses(def, x.name).length;
            return (
              <button key={x.name} type="button" aria-pressed={selected === x.name} onClick={() => setSelected(x.name)} className={cx("w-full text-left px-3 py-2 hover:bg-surface-2", selected === x.name && "bg-accent-soft")}>
                <span className={cx("block font-mono text-sm truncate", bad ? "text-bad" : "text-ink")}>{x.name}</span>
                <span className="block text-xs text-muted truncate">
                  {x.rows.length} row{x.rows.length === 1 ? "" : "s"} · {uses ? `used ${uses}×` : "not used"}
                </span>
              </button>
            );
          })}
          {!arrays.length && <p className="px-3 py-2 text-sm text-muted">No arrays yet. Use one when a step runs once for each item of a fixed list.</p>}
        </div>
        <Button onClick={add}>
          <Plus className="size-4" aria-hidden /> Add an array
        </Button>
      </div>
      {a && (
        <ArrayPanel
          key={a.name}
          def={def}
          a={a}
          issues={issues.filter((i) => i.where === `array ${a.name}`)}
          onChange={(next) => {
            save(arrays.map((x) => (x.name === a.name ? next : x)));
            if (next.name !== a.name) setSelected(next.name);
          }}
          onDelete={() => {
            save(arrays.filter((x) => x.name !== a.name));
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function ArrayPanel({ def, a, issues, onChange, onDelete, onClose }: { def: Definition; a: DataArray; issues: Issue[]; onChange: (a: DataArray) => void; onDelete: () => void; onClose: () => void }) {
  const [name, setName] = useState(a.name);
  const [attr, setAttr] = useState("");
  useEffect(() => setName(a.name), [a.name]);
  const taken = name !== a.name && (def.arrays ?? []).some((x) => x.name === name);
  const uses = arrayUses(def, a.name);
  const commitName = () => {
    // Steps follow a renamed array.
    if (!ID.test(name) || taken || name === a.name) return setName(a.name);
    onChange({ ...a, name });
  };
  const setRow = (i: number, patch: Partial<DataArray["rows"][number]>) => onChange({ ...a, rows: a.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const addAttr = () => {
    const n = attr.trim();
    if (!ID.test(n) || a.attrs.includes(n)) return;
    onChange({ ...a, attrs: [...a.attrs, n], rows: a.rows.map((r) => ({ ...r, attrs: { ...r.attrs, [n]: "" } })) });
    setAttr("");
  };
  const dropAttr = (n: string) => onChange({ ...a, attrs: a.attrs.filter((x) => x !== n), rows: a.rows.map((r) => ({ ...r, attrs: Object.fromEntries(Object.entries(r.attrs).filter(([k]) => k !== n)) })) });
  return (
    <section className="rounded-xl border border-line bg-surface p-4 space-y-4 text-sm" aria-label={`Edit array ${a.name}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted">Array</div>
          <h2 className="font-display font-semibold text-ink text-lg font-mono">{a.name}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" title="Delete" aria-label="Delete array" disabled={uses.length > 0} onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="ghost" title="Close" aria-label="Close editor" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {issues.map((i) => (
        <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.message}
        </p>
      ))}
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block space-y-1">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={cx(input, "font-mono")} value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName} onKeyDown={(e) => e.key === "Enter" && commitName()} disabled={uses.length > 0} title={uses.length ? "Used by steps; remove those uses to rename" : undefined} />
          {taken && <span className="text-xs text-bad">Another array has that name</span>}
        </label>
        <label className="block space-y-1">
          <span className="text-ink-2 font-medium">Description</span>
          <input className={input} value={a.desc ?? ""} onChange={(e) => onChange({ ...a, desc: e.target.value || undefined })} />
        </label>
      </div>
      <div className="space-y-1.5">
        <span className="text-ink-2 font-medium">Attributes</span>
        <div className="flex flex-wrap gap-1.5 items-center">
          {a.attrs.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 font-mono text-xs">
              {n}
              <button type="button" aria-label={`Remove attribute ${n}`} className="text-muted hover:text-ink" onClick={() => dropAttr(n)}>
                <X className="size-3" />
              </button>
            </span>
          ))}
          <input className="w-36 h-8 rounded-md border border-line bg-surface px-2 text-sm font-mono" value={attr} onChange={(e) => setAttr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAttr()} placeholder="New attribute" aria-label="New attribute" />
          <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={addAttr}>
            + Add
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="py-1 pr-2 font-medium w-8">#</th>
              <th className="py-1 pr-2 font-medium">Value</th>
              {a.attrs.map((n) => (
                <th key={n} className="py-1 pr-2 font-medium font-mono">
                  {n}
                </th>
              ))}
              <th className="py-1 pr-2 font-medium">Description</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {a.rows.map((r, i) => (
              <tr key={i}>
                <td className="py-1 pr-2 text-xs text-muted">{i + 1}</td>
                <td className="py-1 pr-2">
                  <input className={cell} value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} aria-label={`Row ${i + 1} value`} />
                </td>
                {a.attrs.map((n) => (
                  <td key={n} className="py-1 pr-2">
                    <input className={cell} value={r.attrs[n] ?? ""} onChange={(e) => setRow(i, { attrs: { ...r.attrs, [n]: e.target.value } })} aria-label={`Row ${i + 1} ${n}`} />
                  </td>
                ))}
                <td className="py-1 pr-2">
                  <input className={cell} value={r.desc ?? ""} onChange={(e) => setRow(i, { desc: e.target.value || undefined })} aria-label={`Row ${i + 1} description`} />
                </td>
                <td>
                  <button type="button" className="p-1.5 rounded-lg text-muted hover:bg-surface-2" aria-label={`Remove row ${i + 1}`} onClick={() => onChange({ ...a, rows: a.rows.filter((_, j) => j !== i) })}>
                    <X className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={() => onChange({ ...a, rows: [...a.rows, { value: "", attrs: Object.fromEntries(a.attrs.map((n) => [n, ""])) }] })}>
        + Row
      </button>
      <p className="text-xs text-muted">{uses.length ? `Used by ${uses.join(", ")}.` : "Not used yet: choose it in a step's Repeat section, or as a field's value."}</p>
    </section>
  );
}
